/**
 * @author Gareth West
 * A tab-friendly, observable local storage.
 */

import { browser } from "$app/environment";
import { concat, defer, filter, fromEvent, map, Observable, of, Subject } from "rxjs";

const KEY = 'mrd';

function getMemoryStorage() {
	if (!browser) {
		console.log('LocalStorage disabled server-side.');
		return null;
	}
	console.warn('Using memory storage. This is not persistent.');
	const map = new Map<string, string>();
	return {
		map: new Map<string, string>(),
		get length() { return map.size; },
		clear: () => map.clear(),
		getItem: k => map.get(k) || null,
		key: i => Array.from(map.keys())[i] || null,
		removeItem: k => map.delete(k),
		setItem: (k,v) => map.set(k,v),
	} as Storage;
}

export const hasLS = () => !!(storageAvailable('localStorage') ? localStorage : getMemoryStorage());

export type StoreItem<T = any> = {key: string | null, value: T | null, prev?: T | null}

const json = (str:string|null|undefined) => {
	if (!str) return null;
	try {
		return JSON.parse(str);
	} catch (error) {/* Ignore */}
	return null;
};

export function createContext(KEY: string, type: 'localStorage' | 'sessionStorage' = 'localStorage') {

	const LS = browser && storageAvailable(type) ? window[type] : getMemoryStorage();
	const stream = new Subject<StoreItem>();

	/**
	 * A core feature is listening for `storage` events emitted when other tabs make changes
	 * to stored items.
	 */
	if (LS && !('map' in LS)) { // if memory storage, don't listen for storage events
		fromEvent<StorageEvent>(window, 'storage').pipe(
			filter(({key}) => !key || key.startsWith(`${KEY}-`)),
			map(({ key, newValue, oldValue }) => ({ key, value: json(newValue), prev: json(oldValue) })),
		).subscribe(stream); // bridge the event stream to the main stream
	}
	
	/**
	 * Persist an object.
	 * @param key Unique object reference name.
	 * @param value Premative or object.
	 * @returns Passed value
	 */
	function setItem<T>(key:string, value?:T) {
		const k = `${KEY}-${key}`;
		const prev = getItem(key);
		if (typeof value === 'undefined') {
			LS?.removeItem(k);
		} else {
			LS?.setItem(k, JSON.stringify(value));
		}
		stream.next({ key: k, value, prev });
		return value;
	}

	/**
	 * Get a persisted object's current state.
	 * @param key Unique object reference name.
	 * @returns Premative or object.
	 */
	function getItem<T = any>(key:string) {
		return json(LS?.getItem(`${KEY}-${key}`) as string) as T;
	}

	/**
	 * Observe changes to a storage item. When the item is removed the value will be `null`.
	 * When the storage is cleared by the user the `key` will be `null`.
	 * @param key Storage key to observe
	 * @returns A stream of value changes. The current value will be immediately next.
	 */
	function observe<T>(key:string, startWithCurrentValue = true):Observable<StoreItem<T>> {
		if (!LS) return of({key, value:null}); // prevent long running observers being created server-side
		const $ = stream.pipe(
			filter(item => !item.key || item.key === `${KEY}-${key}`), // `item.key===null` means a storage clear happened and all observers (of all keys) need to be made aware
			map(item => ({ ...item, key })), // add the key in case of a storage clear (which excludes keys)
		)
		if (!startWithCurrentValue) return $;
		return concat(defer(() => of({ key, value: getItem<T>(key)})), $);
	}

	/**
	 * Get a list of keys that start with a specified string. Handy for creating lists of keys eg. `item-[id]`
	 * @param keyStart The starting string of intrested keys.
	 * @returns A list of keys that start with.
	 */
	function allKeysStartingWith(keyStart:string) {
		if (!LS) return [];
		const keys: string[] = [];
		for (let i = 0; i < LS.length; i++) {
			const key = LS.key(i);
			if (key?.startsWith(`${KEY}-${keyStart}`)) keys.push(key.substring(KEY.length + 1));
		}
		return keys;
	}

	function clear() {
		// LS?.clear();
		if (!LS) return;
		// delete all keys starting with KEY
		let i = 0;
		while (i < LS.length) {
			const key = LS.key(i);
			if (key?.startsWith(`${KEY}-`)) LS?.removeItem(key);
			else i++;
		}
		// notify all observers that their value is now undefined
		stream.next({ key: null, value: undefined });
	}

	return {
		setItem,
		getItem,
		observe,
		allKeysStartingWith,
		clear,
	}
}

const store = createContext(KEY);
export const { setItem, getItem, observe, allKeysStartingWith, clear } = store;

/**
 * Check is storage is available to be used.
 * Taken from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
 * @param type Storage ytpe
 * @returns `true` if storage is available
 */
export function storageAvailable(type:'localStorage'|'sessionStorage'):boolean {
	let storage:Storage|null = null;
	try {
		storage = window[type] ?? globalThis[type];
		const x = '__storage_test__';
		storage.setItem(x, x);
		storage.removeItem(x);
		return true;
	}
	catch (e) {
		return e instanceof DOMException && (
			// everything except Firefox
			e.code === 22 ||
			// Firefox
			e.code === 1014 ||
			// test name field too, because code might not be present
			// everything except Firefox
			e.name === 'QuotaExceededError' ||
			// Firefox
			e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
			// acknowledge QuotaExceededError only if there's something already stored
			(!!storage && storage.length !== 0);
	}
}

